import React, { useCallback, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import styled from "styled-components";
import "./content-styles.css";
import { autoCapitaliseFirstLetter } from "../../Constants/utility";
import { useArticleTracking } from "../../hooks/useArticleTracking";
import { useSlugTitles } from "../../hooks/useSlugTitles";
import { BreadCrumbs } from "../BreadCrumbs";
import TableOfContents from "./TableOfContents";

const ARTICLE_BODY_ID = "article-body-ck";

function slugify(text) {
  if (!text) return "";
  const div = document.createElement("div");
  div.innerHTML = text;
  const raw = div.textContent || div.innerText || "";
  return raw
    .trim()
    .toLowerCase()
    .replaceAll(/[^\w\s-]/g, "")
    .replaceAll(/\s+/g, "-")
    .replaceAll(/-+/g, "-")
    .replaceAll(/(^-|-$)/g, "") || "section";
}

function safeDecodeHash(rawHash) {
  if (!rawHash) return "";
  try {
    return decodeURIComponent(rawHash.replace(/^#/, ""));
  } catch {
    return rawHash.replace(/^#/, "");
  }
}

function getUniqueId(baseId, usedIds) {
  const safeBase = baseId || "section";
  let id = safeBase;
  let suffix = 1;
  while (usedIds.has(id)) {
    id = `${safeBase}-${suffix}`;
    suffix += 1;
  }
  usedIds.add(id);
  return id;
}

function getTimeAgo(date) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  if (diffYears > 0) return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
  if (diffMonths > 0) return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  if (diffDays > 0) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  return "Recently";
}

function getVideoId(url) {
  if (!url) return false;
  const regExp = /^(?:.*)(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*)(?:.*)$/;
  const match = url.match(regExp);
  return match?.[2]?.length === 11 ? match[2] : false;
}

export const ArticlePageWrapper = styled.section`
  width: 100%;
  box-sizing: border-box;
  padding: 0 0 32px;
  font-family: Inter;
  overflow: visible;
`;

/* Match article listing: same vertical spacing so breadcrumbs don’t jump between listing and article */
const BreadcrumbsRow = styled.div`
  margin-top: 0;
  margin-bottom: 20px;
`;

const ArticleLayout = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0;
  width: 100%;
  overflow: visible;
`;

const ArticleColumn = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
`;

const TocColumn = styled.div`
  position: sticky;
  top: 60px;
  align-self: flex-start;
  flex-shrink: 0;
  width: 240px;
  padding-left: 32px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const ArticleMain = styled.main`
  width: 100%;
`;

const ArticleMainBox = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 40px;
  box-sizing: border-box;

  @media (max-width: 778px) {
    padding: 16px 18px 20px;
  }
`;

/* Heading/md/semibold: Inter Semibold, 24px, 600, line-height 28px, #0f172a */
const ArticleTitle = styled.h1`
  font-family: Inter, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 0;
  color: #0f172a;
  margin: 0 0 12px 0;

  @media (max-width: 778px) {
    font-size: 22px;
    line-height: 28px;
  }
`;

/* Body/sm/regular: Inter Regular, 14px, 400, line-height 20px, #475569 */
const ArticleMeta = styled.p`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #475569;
  margin: 0 0 54px 0;
`;

const ArticleBody = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  font-family: Inter, sans-serif;
  overflow-x: hidden;

  /* Headings: inherit font-weight so only the weight from article (admin dashboard) is shown */
  &.ck-content h1 {
    font-family: Inter, sans-serif;
    font-size: 24px;
    font-weight: inherit;
    line-height: 28px;
    letter-spacing: 0;
    color: #0f172a;
    margin: 0 0 24px 0;
  }

  &.ck-content h2 {
    font-family: Inter, sans-serif;
    font-size: 20px;
    font-weight: inherit;
    line-height: 24px;
    letter-spacing: 0;
    color: #0f172a;
    margin: 32px 0 20px 0;
    scroll-margin-top: 24px;
  }

  &.ck-content h3 {
    font-family: Inter, sans-serif;
    font-size: 18px;
    font-weight: inherit;
    line-height: 24px;
    letter-spacing: 0;
    color: #0f172a;
    margin: 24px 0 12px 0;
    scroll-margin-top: 24px;
  }

  /* Body/md/regular: Inter Regular, 16px, 400, line-height 24px */
  &.ck-content p {
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    color: #0f172a;
    margin: 0 0 1em 0;
  }

  &.ck-content ul,
  &.ck-content ol {
    margin: 0 0 16px 0;
    padding-left: 24px;
  }

  &.ck-content li {
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    margin-bottom: 10px;
    color: #0f172a;
  }

  &.ck-content li:last-child {
    margin-bottom: 0;
  }

  /* Callout box: bg #eff6ff, border #bfdbfe, 12px radius, padding 20px, text 16px #1d4ed8 */
  &.ck-content blockquote {
    margin: 24px 0;
    padding: 20px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 12px;
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    color: #1d4ed8;
    font-style: normal;
    text-align: left;
  }

  &.ck-content blockquote p {
    font-family: Inter, sans-serif;
    color: #1d4ed8;
    margin: 0;
  }

  &.ck-content blockquote a {
    color: #1d4ed8;
    text-decoration: none;
  }

  &.ck-content blockquote a:hover {
    text-decoration: underline;
  }

  &.ck-content .image,
  &.ck-content figure {
    margin: 40px 0;
  }

  &.ck-content .image img,
  &.ck-content figure img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  /* Code (inline) and code block – editor code/codeBlock */
  &.ck-content code {
    font-family: ui-monospace, monospace;
    font-size: 13px;
    color: #1a202c;
    background-color: rgba(0, 0, 0, 0.06);
    padding: 0.2em 0.4em;
    border-radius: 4px;
  }
  &.ck-content pre {
    font-family: ui-monospace, monospace;
    font-size: 13px;
    line-height: 1.5;
    color: #1a202c;
    background-color: rgba(0, 0, 0, 0.06);
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    margin: 24px 0;
    overflow-x: auto;
  }
  &.ck-content pre code {
    background: none;
    padding: 0;
  }

  /* Tables – editor insertTable (with .table wrapper) or plain table */
  &.ck-content .table {
    margin: 24px 0;
  }
  &.ck-content .table table,
  &.ck-content table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }
  &.ck-content .table td,
  &.ck-content .table th,
  &.ck-content table td,
  &.ck-content table th {
    font-family: Inter, sans-serif;
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    font-size: 14px;
    line-height: 20px;
    color: #0f172a;
  }
  &.ck-content .table th,
  &.ck-content table th {
    background: #f7fafc;
    font-weight: 600;
  }

  /* Horizontal rule – editor horizontalLine */
  &.ck-content hr {
    margin: 24px 0;
    border: 0;
    border-top: 1px solid #e2e8f0;
  }

  /* Links */
  &.ck-content a {
    color: #1d4ed8;
    text-decoration: none;
  }
  &.ck-content a:hover {
    text-decoration: underline;
  }
`;

const ArticlePrevWrap = styled.div`
  display: block;
  width: 100%;
`;

export default function PreviewArticle({ selectedArticle }) {
  const originalDocTitle = useRef("");
  const [tocItems, setTocItems] = useState([]);
  const [activeTocId, setActiveTocId] = useState(null);

  useArticleTracking(selectedArticle);

  const slugs = selectedArticle?.fullPath?.split("/") ?? [];
  const categorySlugs = slugs.slice(0, -1);
  const { slugTitleMap, isLoading: isSlugTitlesLoading } = useSlugTitles(categorySlugs);

  useEffect(() => {
    originalDocTitle.current = document.title;
    return () => {
      document.title = originalDocTitle.current;
    };
  }, []);

  useEffect(() => {
    const docTitle = selectedArticle?.title;
    if (!docTitle) return;
    document.title = autoCapitaliseFirstLetter(docTitle);
  }, [selectedArticle?.title]);

  /**
   * Given a raw hash string (decoded, with or without "#"),
   * find the best-matching heading id from tocItems.
   */
  const findHeadingIdFromHash = useCallback(
    (rawHash) => {
      const cleaned = safeDecodeHash(rawHash);
      if (!cleaned || !tocItems.length) return null;
      // 1. Exact match
      const exact = tocItems.find((item) => item.id === cleaned);
      if (exact) return exact.id;
      // 2. Slug the hash the same way headings are slugged, then prefix-match
      const hashSlug = slugify(cleaned);
      if (hashSlug) {
        const prefixMatch = tocItems.find((item) => item.id.startsWith(hashSlug));
        if (prefixMatch) return prefixMatch.id;
      }
      // 3. Case-insensitive text match as last resort
      const lower = cleaned.toLowerCase().replace(/[-_]/g, " ");
      const fuzzy = tocItems.find((item) => {
        const itemText = (item.text || "").toLowerCase();
        return itemText === lower || itemText.replace(/[-_]/g, " ") === lower;
      });
      return fuzzy ? fuzzy.id : null;
    },
    [tocItems]
  );

  const resolveTargetId = useCallback(
    (rawHash) => {
      const cleaned = safeDecodeHash(rawHash);
      if (!cleaned) return null;
      if (document.getElementById(cleaned)) return cleaned;
      return findHeadingIdFromHash(cleaned);
    },
    [findHeadingIdFromHash]
  );

  const getHashFromHref = useCallback((href) => {
    if (!href) return "";
    if (href.startsWith("#")) return href.slice(1);
    try {
      const url = new URL(href, globalThis.location.href);
      const isSameDocument =
        url.origin === globalThis.location.origin &&
        url.pathname === globalThis.location.pathname;
      if (!isSameDocument || !url.hash) return "";
      return url.hash.slice(1);
    } catch {
      return "";
    }
  }, []);

  const scrollToSection = useCallback((id) => {
    const section = document.getElementById(id);
    if (!section) return;
    section.scrollIntoView({ behavior: "smooth" });
    // Update the URL hash so the link is shareable
    const nextHash = "#" + id;
    if (globalThis.location.hash !== nextHash) {
      globalThis.history.replaceState(
        null,
        "",
        globalThis.location.pathname + globalThis.location.search + nextHash
      );
    }
  }, []);

  // Build tocItems from article headings
  useEffect(() => {
    const div = document.getElementById(ARTICLE_BODY_ID);
    if (!div) return;
    const headings = div.querySelectorAll("h1, h2, h3");
    const items = [];
    const usedIds = new Set();

    // Reserve ids from non-heading nodes so generated heading ids never collide with them.
    div.querySelectorAll("[id]").forEach((node) => {
      if (node.matches("h1, h2, h3")) return;
      const currentId = (node.getAttribute("id") || "").trim();
      if (currentId) usedIds.add(currentId);
    });

    headings.forEach((el, index) => {
      const tag = el.tagName.toUpperCase();
      const level = tag === "H1" ? 1 : tag === "H2" ? 2 : 3;
      const text = (el.textContent || "").trim();
      const existingId = (el.getAttribute("id") || "").trim();
      const fallbackId = text ? slugify(text) : `heading-${index + 1}`;
      const id = getUniqueId(existingId || fallbackId, usedIds);
      el.id = id;
      items.push({ id, text, level });
    });
    setTocItems(items);
  }, [selectedArticle?.content]);

  // Handle initial page load with a hash in the URL
  useEffect(() => {
    const hash = globalThis.location.hash;
    if (!hash) return;
    const targetId = resolveTargetId(hash);
    if (!targetId) return;
    const timer = setTimeout(() => {
      scrollToSection(targetId);
    }, 300);
    return () => clearTimeout(timer);
  }, [tocItems, resolveTargetId, scrollToSection]);

  // Intercept clicks on in-body anchor links (e.g. "Table of Contents" links in the article HTML)
  useEffect(() => {
    const div = document.getElementById(ARTICLE_BODY_ID);
    if (!div) return;

    const handleClick = (e) => {
      const target = e.target instanceof Element ? e.target : e.target?.parentElement;
      const anchor = target?.closest("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      const rawHash = getHashFromHref(href);
      if (!rawHash) return;

      const targetId = resolveTargetId(rawHash);
      if (targetId) {
        e.preventDefault();
        e.stopPropagation();
        scrollToSection(targetId);
      }
    };

    div.addEventListener("click", handleClick);
    return () => div.removeEventListener("click", handleClick);
  }, [getHashFromHref, resolveTargetId, scrollToSection]);

  const visibilityRef = useRef(new Map());
  const atBottomRef = useRef(false);

  useEffect(() => {
    if (!tocItems.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (atBottomRef.current) return;
        entries.forEach((entry) => {
          const id = entry.target.id;
          visibilityRef.current.set(id, entry.isIntersecting ? entry.boundingClientRect.top : null);
        });
        const visible = [...visibilityRef.current.entries()]
          .filter(([, t]) => t != null)
          .sort((a, b) => a[1] - b[1]);
        if (visible.length > 0) setActiveTocId(visible[0][0]);
      },
      { rootMargin: "-10px 0px -70% 0px", threshold: 0 }
    );

    tocItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [tocItems]);

  useEffect(() => {
    if (!tocItems.length) return;

    const articleBody = document.getElementById(ARTICLE_BODY_ID);
    if (!articleBody) return;

    const lastId = tocItems[tocItems.length - 1].id;

    const checkAtBottom = () => {
      const rect = articleBody.getBoundingClientRect();
      const threshold = 80;
      const atBottom = rect.bottom <= window.innerHeight + threshold;
      atBottomRef.current = atBottom;
      if (atBottom) setActiveTocId(lastId);
    };

    const onScroll = () => requestAnimationFrame(checkAtBottom);

    window.addEventListener("scroll", onScroll, { passive: true });
    checkAtBottom();
    return () => window.removeEventListener("scroll", onScroll);
  }, [tocItems]);

  useEffect(() => {
    const div = document.getElementById(ARTICLE_BODY_ID);
    if (!div) return;
    const onResize = () => {
      const containerWidth = div.offsetWidth;
      div.querySelectorAll("img").forEach((image) => {
        if (image.className !== "") {
          const imageWidth = image?.style?.width?.replace("px", "");
          if (imageWidth && Number.parseFloat(imageWidth) > containerWidth) {
            image.style.width = containerWidth + "px";
          }
        } else {
          image.style.width = containerWidth + "px";
        }
      });
    };
    globalThis.addEventListener("resize", onResize);
    onResize();
    return () => globalThis.removeEventListener("resize", onResize);
  }, [selectedArticle]);

  const Image_Base_Url = process.env.REACT_APP_IMAGE_BASE_URL || "";

  function stripHtml(html) {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    tmp.querySelectorAll("figure").forEach((figure) => {
      const url = figure?.querySelector("oembed")?.getAttribute("url");
      figure.style.width = "auto";
      figure.style.height = "auto";
      if (url) {
        const videoId = getVideoId(url);
        const ifrm = document.createElement("iframe");
        if (videoId) {
          ifrm.setAttribute("src", `https://www.youtube.com/embed/${videoId}`);
        }
        figure.replaceChildren(ifrm);
      }
    });
    tmp.querySelectorAll("img[src]").forEach((img) => {
      const src = img.getAttribute("src") || "";
      if (src && !/^https?:\/\//i.test(src) && Image_Base_Url) {
        const base = Image_Base_Url.endsWith("/") ? Image_Base_Url : Image_Base_Url + "/";
        img.setAttribute("src", base + src.replace(/^\//, ""));
      }
    });
    return tmp.innerHTML;
  }

  const sanitizedTitle = DOMPurify.sanitize(selectedArticle?.title ?? "", {
    ADD_TAGS: ["oembed", "iframe"],
    ADD_ATTR: ["url", "src", "class", "style"],
  });
  const sanitizedContent = DOMPurify.sanitize(selectedArticle?.content ?? "", {
    ADD_TAGS: ["oembed", "iframe"],
    ADD_ATTR: ["url", "src", "class", "style"],
  });

  const slugNameMap = {
    ...slugTitleMap,
    ...(selectedArticle?.slug ? { [selectedArticle?.slug]: selectedArticle?.title } : {}),
  };

  const lastUpdated = getTimeAgo(selectedArticle?.modifiedAt);

  if (!selectedArticle || !Object.keys(selectedArticle).length) return null;

  return (
    <ArticlePageWrapper>
      <BreadcrumbsRow>
        <BreadCrumbs
          fullPath={selectedArticle?.fullPath}
          isLoading={isSlugTitlesLoading}
          slugNameMap={slugNameMap}
        />
      </BreadcrumbsRow>

      <ArticleLayout>
        <ArticleColumn>
          <ArticleMain>
            <ArticleMainBox>
              <ArticleTitle dangerouslySetInnerHTML={{ __html: sanitizedTitle }} />
              {lastUpdated && <ArticleMeta>Last updated {lastUpdated}</ArticleMeta>}
              <ArticleBody id={ARTICLE_BODY_ID} className="ck-content">
                <ArticlePrevWrap
                  dangerouslySetInnerHTML={{ __html: stripHtml(sanitizedContent) }}
                />
              </ArticleBody>
            </ArticleMainBox>
          </ArticleMain>
        </ArticleColumn>
        <TocColumn>
          <TableOfContents items={tocItems} activeId={activeTocId} onScrollToSection={scrollToSection} />
        </TocColumn>
      </ArticleLayout>
    </ArticlePageWrapper>
  );
}
